import { setAuthEmbeddedReturn } from '@/lib/authEmbedded';
import { clearFirstTouch } from '@/lib/firstTouch';
import { logError } from '@/lib/logError';
import { identifyMarketing, trackSignupConversion } from '@/lib/marketing';
import { redirect } from '@/lib/redirect';
import { isInviteRole, shouldSkipTrial } from '@/lib/roles';
import type { SignUpOptions } from '@/stores/auth';
import { useBillingStore } from '@/stores/billing';
import { useUserStore } from '@/stores/user';
import type { User } from '@/types/user';
import { useRoute, useRouter } from 'vue-router';

/** What an auth provider (password signup, SSO) hands back on success. */
export type SsoSuccessPayload = {
  user: Partial<User>;
  isNewUser: boolean;
};

/**
 * Signup intent resolved from the URL / session (`?trial=`, `?role=`,
 * `?referralCode=` stashed by `pullSessionFromUrl`). Computed the same way on
 * the login and signup views: a first-timer can click Google on either page.
 */
export type SignupContext = {
  role?: string;
  /** `trial=none` or a non-author invite role — skip the Stripe trial. */
  trialNone: boolean;
  /** Invited collaborator (beta reader / editor) — skip conversion tracking. */
  invited: boolean;
  referralCode: string;
  /** Options for `authStore.signUp` / `authStore.ssoLogin`. */
  signUpOptions?: SignUpOptions;
};

/**
 * Shared post-authentication flow for the auth views.
 *
 * Whether an authentication came from the password form or an SSO button, the
 * follow-up depends only on whether the account is new (`isNewUser`), NOT on
 * which view hosted the control — returning users click Google on the signup
 * page and vice versa:
 *
 * - existing user → store the user + `redirect()` back to the app.
 * - new user      → SignupView's post-signup sequence (conversion tracking,
 *   billing sync + referral coupon on trials, onboarding).
 */
export function useAuthFlow() {
  const route = useRoute();
  const router = useRouter();
  const userStore = useUserStore();
  const billingStore = useBillingStore();

  /**
   * Live query first, then the sessionStorage copy `pullSessionFromUrl`
   * stashed — the query is stripped during the cross-origin round trip.
   * Mirrors `SignupView.readQueryOrSession` / `AuthInviteBanner`.
   */
  function readQueryOrSession(key: string): string {
    const q = route.query[key];
    if (q !== undefined && q !== null && q !== '') {
      return String(Array.isArray(q) ? q[0] : q).trim();
    }
    const s = sessionStorage.getItem(key);
    return s ? String(s).trim() : '';
  }

  /** Resolve the signup intent at call time (session values are not reactive). */
  function resolveSignupContext(): SignupContext {
    const role = readQueryOrSession('role') || undefined;
    const trialNone = readQueryOrSession('trial').toLowerCase() === 'none' || shouldSkipTrial(role);
    const referralCode =
      readQueryOrSession('referralCode') || (typeof route.query.ref === 'string' ? route.query.ref : '') || '';

    const signUpOptions: SignUpOptions | undefined =
      role || !trialNone ? { ...(role ? { role } : {}), ...(!trialNone ? { trial: true as const } : {}) } : undefined;

    return { role, trialNone, invited: isInviteRole(role), referralCode, signUpOptions };
  }

  /**
   * Hand the freshly authenticated user off to their redirect target.
   *
   * `redirect()` navigates away itself, but it can throw on an invalid stored
   * URL. The caller here is a form, so an
   * escaping rejection surfaces as an error message on the login screen and
   * strands a user who *is* already signed in. The billing portal is always
   * reachable once authenticated, so fall back to it, the same way the router
   * guard's `handOff()` does.
   */
  async function handOff() {
    try {
      await redirect();
    } catch (err) {
      logError('post-auth redirect failed, falling back to the billing portal', err);
      await router.push('/billing/portal');
    }
  }

  /**
   * Complete a successful authentication. `context` describes the signup
   * intent and is only consulted when `isNewUser` is true; it defaults to a
   * fresh `resolveSignupContext()`.
   */
  async function completeAuth(user: Partial<User>, isNewUser: boolean, context?: SignupContext) {
    userStore.setUser(user);

    if (!isNewUser) {
      await handOff();
      return;
    }

    const { trialNone, invited, referralCode } = context ?? resolveSignupContext();

    // The first touch went out with the signup request; a second account made
    // on this device later must not inherit it.
    clearFirstTouch();

    // Registration conversion (GA sign_up + Ads, FB CompleteRegistration,
    // Pinterest signup). Identify first — synchronously, ahead of App.vue's
    // next-flush watcher — so the conversion carries user_id / enhanced-match
    // email / external_id. Skip invited collaborators: they're not acquisition
    // signups. No-op off the production host.
    if (!invited) {
      if (user.uid) identifyMarketing({ uid: user.uid, email: user.email });
      trackSignupConversion();
    }
    sessionStorage.removeItem('trial');
    sessionStorage.removeItem('role');

    if (trialNone) {
      sessionStorage.removeItem('referralCode');
      await handOff();
      return;
    }

    // Stripe customer + trial were provisioned server-side as part of the
    // signup tasks. Sync local billing state so downstream views (and the
    // coupon flow below) see the new subscription.
    await billingStore.fetchBillingOverview();

    if (referralCode) {
      await billingStore.applyCouponCode(referralCode);
    }
    sessionStorage.removeItem('referralCode');

    setAuthEmbeddedReturn();
    await router.push('/account/onboarding');
  }

  return { resolveSignupContext, completeAuth };
}
